import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Opportunities.css';

const GREENHOUSE_BOARD = 'inalabconsulting';
const API_BASE = `https://boards-api.greenhouse.io/v1/boards/${GREENHOUSE_BOARD}`;
const API_URL = process.env.REACT_APP_API_URL;
const HCAPTCHA_SITEKEY = '6ed0b286-1ad8-46ae-81f2-b2b6a2ec2e00';

function decodeHtmlEntities(str) {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return doc.body.textContent;
}

function stripBoilerplate(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('.content-intro, .content-conclusion').forEach(el => el.remove());
  return doc.body.innerHTML.trim();
}

const BENEFITS = [
  ['Professional Development and Training', 'Employee Referral Program'],
  ['Incentive Award Program', 'Health Benefits'],
  ['401K match', 'Life and Disability Insurance'],
  ['Acknowledgement Bonus Program', 'Paid Time Off'],
  ['Employee Assistance Program', null],
];

const ETP_GROUPS = [
  {
    name: 'Technology & Data',
    roles: [
      {
        title: 'Forward Deployed Engineer',
        count: '2-3',
        blurb: 'Build and ship AI and Generative AI applications - LLMs, RAG, AI agents, and intelligent workflows - for real business and Federal missions alongside senior engineers and solution architects.',
        whatYoullDo: [
          'Develop AI and Generative AI applications supporting real-world business and Federal Government use cases.',
          'Build and enhance LLM-based applications, RAG solutions, AI agents, and intelligent workflows.',
          'Integrate AI models with APIs, databases, documents, and enterprise applications.',
          'Prototype new AI concepts and rapidly turn ideas into working demonstrations.',
          'Test, evaluate, troubleshoot, and improve AI application performance.',
          'Work with experienced engineers and solution architects on customer-facing solutions.',
          'Document code, designs, and technical findings.',
        ],
        skills: [
          'Python programming.',
          'Understanding of LLMs and Generative AI.',
          'Familiarity with RAG, AI agents, prompt engineering, and APIs.',
          'Exposure to frameworks such as LangChain, LlamaIndex, or similar tools.',
          'Git/GitHub and basic software-development practices.',
          'Strong problem-solving skills and willingness to experiment and learn.',
        ],
        experience: '0-3 years. Current undergraduate/graduate students and recent graduates are encouraged to apply. Personal projects, GitHub repositories, hackathons, research, and internships count.',
        highlight: { label: 'Big Plus', text: 'Show us an AI application you have actually built.' },
      },
      {
        title: 'Data Scientist / Analyst',
        count: '2-3',
        blurb: 'Turn structured and unstructured data into insight - building dashboards, visualizations, and analytical models with Python, SQL, and BI tools to support AI and engineering teams.',
        whatYoullDo: [
          'Analyze structured and unstructured datasets to identify patterns, trends, and actionable insights.',
          'Clean, transform, combine, and validate data from multiple sources.',
          'Develop dashboards, reports, visualizations, and analytical models.',
          'Use Python and SQL to perform exploratory and statistical analysis.',
          'Support development of predictive analytics and machine-learning solutions.',
          'Work with AI and engineering teams to prepare data for AI applications.',
          'Present findings in a way that technical and business users can understand.',
        ],
        skills: [
          'Python and SQL.',
          'Pandas, NumPy, or similar data-analysis libraries.',
          'Power BI, Tableau, or similar visualization tools.',
          'Basic statistics and data-analysis concepts.',
          'Familiarity with machine-learning concepts.',
          'Excel and strong analytical/problem-solving ability.',
          'Ability to communicate what the data actually means.',
        ],
        experience: '0-3 years. Students studying Data Science, Computer Science, Statistics, Mathematics, Engineering, Economics, or related disciplines are encouraged to apply.',
        highlight: { label: 'Big Plus', text: 'Bring us a project where you turned messy data into a useful insight.' },
      },
      {
        title: 'Automation Test Engineer',
        count: '4-6',
        blurb: 'Automate testing for web applications and APIs using modern frameworks, integrate tests into CI/CD pipelines, and work with developers to raise software quality.',
        whatYoullDo: [
          'Develop automated tests for web applications, APIs, and software solutions.',
          'Create and maintain test scripts using modern automation frameworks.',
          'Perform functional, regression, integration, and API testing.',
          'Identify, document, reproduce, and help troubleshoot software defects.',
          'Integrate automated testing into CI/CD pipelines.',
          'Work directly with developers to improve software quality.',
          'Explore AI-assisted approaches for generating and maintaining automated tests.',
        ],
        skills: [
          'Selenium, Playwright, Cypress, or similar automation frameworks.',
          'Python, Java, JavaScript, or another programming language.',
          'API testing and tools such as Postman.',
          'Understanding of software testing concepts.',
          'Git/GitHub.',
          'Basic familiarity with CI/CD.',
          'Strong attention to detail and a natural instinct for finding problems.',
        ],
        experience: '0-3 years. Coursework, internships, personal automation projects, or software-development experience can substitute for formal QA experience.',
        highlight: { label: 'Big Plus', text: 'Show us something you have automated.' },
      },
    ],
  },
  {
    name: 'Business & Operations',
    roles: [
      {
        title: 'Junior Federal Account Manager',
        count: '4-6',
        blurb: 'Help develop and grow relationships with Federal customers and partners - researching agencies and opportunities, preparing briefings, and managing CRM while learning Inalab solutions.',
        whatYoullDo: [
          'Help develop and grow relationships with Federal Government customers and industry partners.',
          'Research agencies, programs, decision-makers, budgets, contracts, and upcoming opportunities.',
          'Support account plans and customer engagement strategies.',
          'Help prepare customer presentations, capability briefings, and demonstrations.',
          'Coordinate meetings and track follow-up actions.',
          'Maintain customer, opportunity, and activity information in the CRM.',
          'Work with technical teams to understand Inalab AI, data, automation, cybersecurity, and IT solutions.',
          'Attend industry events, conferences, customer meetings, and networking activities.',
          'Gradually develop responsibility for assigned accounts and opportunities.',
        ],
        skills: [
          'Excellent communication and interpersonal skills.',
          'Confidence meeting and speaking with new people.',
          'Strong research and analytical skills.',
          'PowerPoint, Excel, Word, and modern AI productivity tools.',
          'Strong follow-through and organizational ability.',
          'Interest in technology, Federal Government, consulting, or sales.',
          'Self-motivated personality with a desire to build relationships and win business.',
        ],
        experience: '0-3 years. Business, Marketing, Communications, Government, Political Science, Information Technology, or related majors are welcome. Prior Federal experience is not required.',
        highlight: { label: 'What matters most', text: 'Initiative, communication, curiosity, persistence, and willingness to get out and meet customers.' },
      },
      {
        title: 'Recruiting / Talent Associate',
        count: '1-2',
        blurb: 'Support Inalab talent team - sourcing candidates, LinkedIn outreach, coordinating interviews, and keeping hiring organized and moving.',
        whatYoullDo: [
          'Source and identify candidates across job boards, LinkedIn, and referrals.',
          'Coordinate and schedule interviews and keep candidates informed.',
          'Help screen applicants and maintain applicant tracking and notes.',
          'Support job postings, outreach campaigns, and hiring events.',
          'Communicate with candidates and hiring managers throughout the process.',
        ],
        skills: [
          'Candidate sourcing and LinkedIn.',
          'Interviewing and communication.',
          'Strong organization and follow-through.',
          'Interest in people, hiring, and talent.',
        ],
        experience: '0-3 years. Students and recent graduates are encouraged to apply.',
      },
      {
        title: 'Executive Assistant + Office Manager',
        count: '1',
        blurb: 'Keep the office running and support leadership - scheduling, events, and day-to-day operations using Office 365 and modern AI tools.',
        whatYoullDo: [
          'Provide scheduling and calendar support for executives.',
          'Manage day-to-day office operations and coordinate events.',
          'Organize documents, communications, and follow-ups.',
          'Use Office 365 and AI productivity tools to streamline work.',
          'Support leadership with administrative and operational tasks.',
        ],
        skills: [
          'Strong organization and attention to detail.',
          'Office 365 and modern AI productivity tools.',
          'Scheduling, events, and executive support.',
          'Clear written and verbal communication.',
        ],
        experience: '0-3 years. Students and recent graduates are encouraged to apply.',
      },
    ],
  },
];

function EtpApplyModal({ role, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [resume, setResume] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const renderWidget = () => {
      if (!window.hcaptcha || !window.hcaptcha.render || !captchaRef.current) return;
      if (widgetIdRef.current !== null) return;
      widgetIdRef.current = window.hcaptcha.render(captchaRef.current, {
        sitekey: HCAPTCHA_SITEKEY,
        callback: (token) => { setCaptchaToken(token); setError(''); },
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
    };
    if (window.hcaptcha && window.hcaptcha.render) {
      renderWidget();
      return;
    }
    const SCRIPT_ID = 'hcaptcha-api';
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderWidget);
    return () => script.removeEventListener('load', renderWidget);
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken('');
    if (window.hcaptcha && widgetIdRef.current !== null) {
      try { window.hcaptcha.reset(widgetIdRef.current); } catch (e) { /* noop */ }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !resume) {
      setError('Please complete all fields and attach your resume.');
      return;
    }
    if (!captchaToken) {
      setError('Please complete the captcha.');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('email', email);
      fd.append('position', role);
      fd.append('resume', resume);
      fd.append('hcaptchaToken', captchaToken);
      const res = await fetch(`${API_URL}/api/etp-apply`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.success) {
        setDone(true);
      } else {
        setError((data && data.error) || 'Submission failed. Please try again.');
        resetCaptcha();
      }
    } catch {
      setError('An error occurred. Please try again.');
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="etp-modal-overlay" onClick={onClose}>
      <div
        className="etp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="etp-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <button className="etp-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        {done ? (
          <div className="etp-modal-success">
            <h3 id="etp-modal-title">Application sent!</h3>
            <p>Thanks for applying for <strong>{role.replace(/\s*\([^)]*\)\s*$/, '')}</strong>. Our talent team will be in touch.</p>
            <button className="etp-apply-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <h3 id="etp-modal-title" className="etp-modal-title">Apply: {role}</h3>
            <div className="etp-field">
              <label htmlFor="etp-name">Name</label>
              <input
                id="etp-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div className="etp-field">
              <label htmlFor="etp-email">Email</label>
              <input
                id="etp-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div className="etp-field">
              <label htmlFor="etp-resume">Resume (PDF, DOC, or DOCX)</label>
              <input
                id="etp-resume"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={e => setResume(e.target.files[0] || null)}
                disabled={loading}
                required
              />
            </div>
            <div className="etp-field etp-captcha">
              <div ref={captchaRef} className="h-captcha-widget" />
            </div>
            {error && <div className="form-error-message" role="alert">{error}</div>}
            <button type="submit" className="etp-apply-btn etp-modal-submit" disabled={loading}>
              {loading ? 'Sending...' : 'Submit Application'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ViewToggle({ view }) {
  return (
    <div className="talent-toggle" role="tablist" aria-label="Join our Team views">
      <Link
        to="/opportunities"
        className={`talent-toggle-btn${view === 'opportunities' ? ' talent-toggle-btn-active' : ''}`}
        role="tab"
        aria-selected={view === 'opportunities'}
      >
        Opportunities
      </Link>
      <Link
        to="/emergingtalentprogram"
        className={`talent-toggle-btn${view === 'etp' ? ' talent-toggle-btn-active' : ''}`}
        role="tab"
        aria-selected={view === 'etp'}
      >
        Emerging Talent Program
      </Link>
    </div>
  );
}

export default function Opportunities({ view = 'opportunities' }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [etpRole, setEtpRole] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const tileRefs = useRef({});

  useEffect(() => {
    document.title = view === 'etp' ? 'Emerging Talent Program' : 'Join our Team';
    window.scrollTo(0, 0);
  }, [view]);

  useEffect(() => {
    if (view !== 'opportunities') return;
    fetch(`${API_BASE}/jobs?content=true`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load jobs');
        return res.json();
      })
      .then(data => {
        setJobs(data.jobs || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [view]);

  const toggleJob = useCallback((id) => {
    setExpandedId(prev => {
      const next = prev === id ? null : id;
      if (next !== null) {
        requestAnimationFrame(() => {
          tileRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      return next;
    });
  }, []);

  if (view === 'etp') {
    return (
      <>
        <div className="page-banner" style={{ backgroundImage: 'url(/opportunities_banner.webp)' }}>
          <h1 className="page-banner-title" id="opportunities-banner-heading">
            Emerging Talent Program
          </h1>
        </div>
        <section className="opportunities-page-wrapper" aria-labelledby="opportunities-banner-heading">
          <ViewToggle view={view} />

          <div className="etp-hero">
            <p className="etp-tagline">Build your career. Build technology that matters.</p>
            <p className="etp-eyebrow">Paid Internships&nbsp;/&nbsp;Part-Time&nbsp;/&nbsp;Full-Time</p>
            <p className="etp-lede">
              For curious students, recent graduates and early-career professionals. Work on real AI,
              data, automation and federal technology initiatives alongside experienced leaders.
            </p>
            <p className="etp-location">
              Tysons&nbsp;/&nbsp;Vienna, VA &middot; Metro accessible &middot; Hybrid 2&ndash;3 days/week &middot; Flexible around classes
            </p>
          </div>

          <h2 className="etp-section-heading">Find your role</h2>
          <p className="etp-section-sub">
            Select a role to read the full description, then apply with your resume and details.
          </p>

          {ETP_GROUPS.map(group => (
            <div className="etp-group" key={group.name}>
              <h3 className="etp-group-heading">{group.name}</h3>
              <div className="etp-role-stack">
                {group.roles.map(role => {
                  const isOpen = expandedRole === role.title;
                  const panelId = `etp-panel-${role.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
                  return (
                    <div
                      key={role.title}
                      className={`etp-role-card${isOpen ? ' etp-role-card-open' : ''}`}
                    >
                      <button
                        type="button"
                        className="etp-role-head"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => setExpandedRole(prev => (prev === role.title ? null : role.title))}
                      >
                        <span className="etp-role-heading">
                          <span className="etp-role-title">{role.title}</span>
                          <span className="etp-role-count">{role.count} {role.count === '1' ? 'position' : 'positions'} available</span>
                          <span className="etp-role-blurb">{role.blurb}</span>
                        </span>
                        <span className="etp-role-chevron" aria-hidden="true">{isOpen ? '\u2212' : '+'}</span>
                      </button>

                      {isOpen && (
                        <div className="etp-role-detail" id={panelId}>
                          {role.whatYoullDo && (
                            <>
                              <h4 className="etp-detail-heading">What You'll Do</h4>
                              <ul className="etp-detail-list">
                                {role.whatYoullDo.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </>
                          )}
                          {role.skills && Array.isArray(role.skills) && (
                            <>
                              <h4 className="etp-detail-heading">Skills We're Looking For</h4>
                              <ul className="etp-detail-list">
                                {role.skills.map((item, i) => <li key={i}>{item}</li>)}
                              </ul>
                            </>
                          )}
                          {role.experience && (
                            <p className="etp-detail-note"><strong>Experience:</strong> {role.experience}</p>
                          )}
                          {role.highlight && (
                            <p className="etp-detail-note"><strong>{role.highlight.label}:</strong> {role.highlight.text}</p>
                          )}
                          <button
                            type="button"
                            className="etp-apply-btn"
                            onClick={() => setEtpRole(`${role.title} (${role.count})`)}
                          >
                            Apply for this role &rarr;
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="etp-closing">
            <h3>Show us what you can do.</h3>
            <p>
              Bring what you have built, automated, analyzed, organized or improved. There is no general
              application - open a role above and apply directly to the position that fits you.
            </p>
            <p className="etp-apply-above">Apply Above</p>
          </div>
        </section>

        {etpRole && <EtpApplyModal role={etpRole} onClose={() => setEtpRole(null)} />}
      </>
    );
  }

  return (
    <>
      <div className="page-banner" style={{ backgroundImage: 'url(/opportunities_banner.webp)' }}>
        <h1 className="page-banner-title" id="opportunities-banner-heading">
          Opportunities
        </h1>
      </div>
      <section className="opportunities-page-wrapper" aria-labelledby="opportunities-banner-heading">
        <ViewToggle view={view} />

        <h2 className="working-at-heading">Working at Inalab</h2>
        <div className="working-at-text">
          <p>
            Inalab is an employee-focused, customer-driven, growth-oriented company. Our fast-paced
            environment is built upon creativity and innovation, professional responsibility, and a
            pursuit of &ldquo;pervasive excellence.&rdquo; Since our founding in 2005, we have prided ourselves
            on providing the highest level of service to our clients. We understand that this level
            of service is achievable only with stellar talent dedicated to pursuing excellence in
            everything they do.
          </p>
          <p>
            At Inalab, you will find challenging work, and sharp, talented professionals to work
            with &ndash; a potent combination, proven to make a difference. Our people are collaborative,
            innovative and agile, and demand excellence from themselves and others. They expect
            nothing less from their employment experience. At Inalab, we consider it our
            responsibility to provide an exceptional environment where talented individuals like you
            can thrive. Our commitment to our employees, teamwork, and variety keep people here. We
            offer our employees continual opportunities for personal and professional growth and
            enrichment.
          </p>
          <p>
            Inalab is built on creativity and innovation, professional responsibility, and a pursuit
            of &ldquo;pervasive excellence.&rdquo; We offer an exciting and collaborative culture that enables
            our people to do challenging and meaningful work while enjoying a strong sense of
            community and fun.
          </p>
        </div>

        <h3 className="benefits-heading">Benefits Include:</h3>
        <table className="benefits-table">
          <tbody>
            {BENEFITS.map(([left, right], i) => (
              <tr key={i}>
                <td>{left}</td>
                <td>{right || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="eeo-statement">
          <strong>Inalab is an Equal Opportunity/Affirmative Action employer.</strong>
        </p>

        <hr className="section-divider" />

        <h2 className="openings-heading">Current Openings</h2>

        {loading && <p className="jobs-loading">Loading positions...</p>}
        {error && <p className="jobs-error">Unable to load positions. Please try again later.</p>}

        {!loading && !error && jobs.length === 0 && (
          <p className="jobs-empty">No open positions at this time. Please check back soon.</p>
        )}

        <div className="job-listings-container">
          {jobs.map(job => {
            const isExpanded = expandedId === job.id;
            const department = job.departments?.[0]?.name;
            const locations = job.offices?.map(o => o.name).join(' | ') || job.location?.name;

            return (
              <div
                key={job.id}
                ref={el => { tileRefs.current[job.id] = el; }}
                className={`job-tile${isExpanded ? ' job-tile-expanded' : ''}`}
                onClick={() => toggleJob(job.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleJob(job.id); }}}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
              >
                <div className="job-tile-header">
                  <div className="job-tile-info">
                    {department && <span className="job-department">{department}</span>}
                    <h3 className="job-title">{job.title}</h3>
                    <span className="job-location">{locations}</span>
                  </div>
                  <span className={`job-chevron${isExpanded ? ' job-chevron-open' : ''}`} aria-hidden="true">&#9662;</span>
                </div>

                {isExpanded && (
                  <div className="job-detail" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="job-description"
                      dangerouslySetInnerHTML={{ __html: stripBoilerplate(decodeHtmlEntities(job.content)) }}
                    />
                    <a
                      href={job.absolute_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="job-apply-btn"
                    >
                      Apply for this Position
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <hr className="section-divider" />

        <h2 className="compliance-heading">Federal Compliance Notices</h2>
        <p className="compliance-intro">
          Inalab Consulting, Inc. participates in the E-Verify program and complies with federal
          regulations regarding workplace discrimination and pay transparency.
        </p>
        <ul className="compliance-links">
          <li>
            <a href="https://www.e-verify.gov/sites/default/files/everify/posters/EVerifyParticipationPoster.pdf"
               target="_blank" rel="noopener noreferrer">
              E-Verify Participation Poster
            </a>
          </li>
          <li>
            <a href="https://www.eeoc.gov/sites/default/files/2023-06/22-088_EEOC_KnowYourRights6.12ScreenRdr.pdf"
               target="_blank" rel="noopener noreferrer">
              Know Your Rights: Workplace Discrimination Poster
            </a>
          </li>
          <li>
            <a href="https://www.act.org/content/dam/act/unsecured/documents/Pay%20Transparency_Notice.pdf"
               target="_blank" rel="noopener noreferrer">
              Pay Transparency Nondiscrimination Provision
            </a>{' '}
            (if applicable)
          </li>
          <li>
            <a href="https://www.e-verify.gov/sites/default/files/everify/posters/IER_RightToWorkPoster%20Eng_Es.pdf"
               target="_blank" rel="noopener noreferrer">
              Your Right to Work
            </a>
          </li>
        </ul>
        <p className="compliance-contact">
          If you have questions about these notices or need assistance, please contact our HR team
          at <a href="mailto:hr@inalabgroup.com">hr@inalabgroup.com</a>.
        </p>
      </section>
    </>
  );
}
